import { useToast } from "@/hooks/use-toast"
import {
  InviteMemberFormData,
  Member,
  MemberRole,
  MemberStatus,
} from "../../types/memberTypes"
import { getPermissionsByRole } from "../../services/permissionService"
import { sendUserInvite } from "@/services/auth/userManagement"
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore"
import { db } from "@/utils/firebase"
import { USERS_COLLECTION } from "@/utils/firestore"

export const useMemberAddition = (
  setMembers: React.Dispatch<React.SetStateAction<Member[]>>,
  setShowAddMemberDialog: (state: boolean) => void,
  resetInviteMemberForm: () => void,
  entityId?: string,
  source?: "portfolio" | "business"
) => {
  const { toast } = useToast()

  // Add member action - explicitly log actions for debugging
  const handleAddMember = () => {
    console.log("handleAddMember called in useMemberAddition")

    // Reset form first
    resetInviteMemberForm()

    // Set dialog open state to true
    setShowAddMemberDialog(true)

    console.log("Dialog should now be open, state set to:", true)
  }

  // Create new member
  const handleAddMemberSubmit = async (formData: InviteMemberFormData) => {
    console.log("handleAddMemberSubmit called with data:", formData)

    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast({
        title: "Invalid Form",
        description: "Please fill out all required fields.",
      })
      return
    }
    if (!source) return toast({ title: "Error", description: "No source" })

    try {
      // Send invitation to user
      if (entityId) {
        console.log("Sending user invite with entityId:", entityId)
        const result = await sendUserInvite(
          formData.email,
          formData.firstName,
          formData.lastName,
          formData.role,
          entityId,
          formData.permissions,
          formData.phone,
          source
        )

        const usersRef = collection(db, "users")
        const q = query(usersRef, where("email", "==", formData.email))
        const querySnapshot = await getDocs(q)

        // Create new member
        const newMember: Member = {
          id: querySnapshot.docs[0].id,
          userId: querySnapshot.docs[0].id,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          role: formData.role as MemberRole,
          permissions: formData.permissions,
          status: "invited" as MemberStatus,
          isCurrentUser: false,
          userExists: false, // Changed from memberExists to userExists
          avatarInitials: `${formData.firstName.charAt(
            0
          )}${formData.lastName.charAt(0)}`,
        }
        setMembers((prev) => [...prev, newMember])
        toast({
          title: "Member Invited",
          description: `${newMember.firstName} ${newMember.lastName} has been invited.`,
        })

        if (!result.success) {
          throw new Error(result.error || "Failed to send invitation")
        }

        console.log("User invite sent successfully:", result)
      } else {
        console.warn("No entityId provided, skipping sendUserInvite")
      }
      // Close dialog
      setShowAddMemberDialog(false)
    } catch (error) {
      console.error("Error sending invitation:", error)
      toast({
        title: "Invitation Failed",
        description:
          error instanceof Error ? error.message : "Failed to send invitation",
        variant: "destructive",
      })
    }
  }

  return {
    handleAddMember,
    handleAddMemberSubmit,
  }
}
